<template lang="pug">
div(
    class="overflow-hidden"
    :class="{ 'relative inline-block': playOnScroll }")
    video(
        ref="video"
        v-bind="videoAttributes"
        :poster="posterSource"
        @canplay="canPlay=true"
        @ended="onEnd"
        class="h-full object-cover"
    )
        source(
            :src="videoSource"
            :type="videoType"
        )
    div(v-if="playOnScroll && videoEnded" class="absolute bottom-3 right-3")
        div(class="flex h-full align-middle")
            img(
                src="@/assets/replay.svg"
                alt="Replay"
                width="36"
                height="36"
                @click="play"
                class="m-auto cursor-pointer"
            )
</template>

<script>
import { intersectionObserverMixin } from '@/helpers/intersectionObserverMixin'

export default {
    name: 'LazyVideo',
    mixins: [intersectionObserverMixin],
    props: {
        videoUrl: {
            type: String,
            required: true,
        },
        posterImageUrl: {
            type: String,
            default: '',
        },
        isLazy: {
            type: Boolean,
            default: false,
        },
        // Background videos use this component but are not play on scroll.
        playOnScroll: {
            type: Boolean,
            default: false,
        },
        replayOnScroll: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLazyLoaded: false,
            videoEnded: false,
            canPlay: false,
            retried: 0,
        }
    },
    computed: {
        videoAttributes() {
            const obj = {
                playsinline: true,
                muted: true,
                width: '100%',
                height: '100%',
            }

            // Determine the W/H based on the Poster Image
            if (this.posterImageUrl) {
                const { width, height } = this.$storyblok.getAssetDimensionsFromUrl(
                    this.posterImageUrl
                )
                obj.width = width
                obj.height = height
            }

            if (this.playOnScroll) {
                obj.muted = true
                obj.preload = 'metadata'
            } else {
                // Background videos autoplay on a loop
                obj.autoplay = true
                obj.loop = true
                obj.preload = 'none'
            }

            return obj
        },
        videoType() {
            if (this.videoUrl) return 'video/' + this.videoUrl.split('.').pop()
            return 'video/mp4'
        },
        videoSource() {
            if (this.isLazyLoaded) return this.videoUrl
            return undefined
        },
        posterSource() {
            if (this.isLazyLoaded) return this.posterImageUrl
            return undefined
        },
    },
    watch: {
        videoSource() {
            // Need to call the load function on the <video> element to get it to autoplay after src change
            this.$refs.video.load()
        },
    },
    created() {
        this.isLazyLoaded = !this.isLazy
    },
    mounted() {
        if (this.isLazy) {
            // When the Video is within 300px of the users scroll position
            // set this.isLazyLoaded = true which will cause the videoSource to be set
            // and trigger the Watcher to load() the video.
            this.observeOnce(this.$refs.video, this.lazyload, { rootMargin: '600px' })
        }

        if (this.playOnScroll)
            // When most (threshold .8) of the video is within the viewport we want to play it
            // and when it exits the viewport threshold we want to pause it.
            this.observe(this.$refs.video, this.tryPlay, this.pause, {
                threshold: 0.8,
            })
    },
    methods: {
        onEnd() {
            // The video has reached its natural end.
            this.videoEnded = true
        },
        lazyload() {
            this.isLazyLoaded = true
        },
        tryPlay() {
            // We do not want to replay on scroll if replayOnScroll is false
            // and the video has played through once already.
            if (!this.replayOnScroll && this.videoEnded) {
                return
            }

            // If the video is loaded and ready, play it!
            if (this.canPlay) {
                this.play()
                return
            }

            // if not ready, wait a bit and try again
            // Retry up to 20 times or give up
            if (this.retried >= 20) return

            setTimeout(() => {
                this.tryPlay()
            }, 250)
        },
        play() {
            this.$refs.video.play()
            this.videoEnded = false
        },
        pause() {
            this.$refs.video.pause()
        },
    },
}
</script>
